
import { useEffect, useRef, useState } from 'react';

export default function About() {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({ projects: 0, countries: 0, years: 0 });
  const sectionRef = useRef(null);

  const countries = [
    { name: 'Uganda', flag: '🇺🇬' },
    { name: 'Kenya', flag: '🇰🇪' },
    { name: 'Tanzania', flag: '🇹🇿' },
    { name: 'Rwanda', flag: '🇷🇼' },
    { name: 'South Sudan', flag: '🇸🇸' }
  ];

  const advantages = [
    { icon: 'ri-stack-line', title: 'Integrated Architecture', desc: 'Full-spectrum execution from strategy to production' },
    { icon: 'ri-global-line', title: 'Regional Intelligence', desc: 'Deep East African market understanding' },
    { icon: 'ri-award-line', title: 'Dual Mastery', desc: 'Traditional and modern communication systems' },
    { icon: 'ri-fingerprint-line', title: 'Strategic Positioning', desc: 'Brands understood, trusted, and chosen' }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          const duration = 2000;
          const steps = 60;
          const interval = duration / steps;
          let step = 0;

          const timer = setInterval(() => {
            step++;
            const progress = step / steps;
            const easeOut = 1 - Math.pow(1 - progress, 3);

            setCounters({
              projects: Math.round(150 * easeOut),
              countries: Math.round(5 * easeOut),
              years: Math.round(5 * easeOut)
            });

            if (step >= steps) clearInterval(timer);
          }, interval);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section ref={sectionRef} id="about" className="relative py-12 sm:py-16 lg:py-20 bg-[#FAFAF9] overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[300px] sm:w-[400px] lg:w-[500px] h-[300px] sm:h-[400px] lg:h-[500px] bg-gradient-to-bl from-[#038C7F]/5 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[200px] sm:w-[250px] lg:w-[300px] h-[200px] sm:h-[250px] lg:h-[300px] bg-gradient-to-tr from-[#F2780C]/5 to-transparent rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className={`mb-8 sm:mb-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#038C7F]/10 rounded-lg sm:rounded-xl flex items-center justify-center">
              <i className="ri-seedling-line text-lg sm:text-xl text-[#038C7F]"></i>
            </div>
            <span className="text-[10px] sm:text-xs font-bold tracking-[0.15em] sm:tracking-[0.2em] text-[#038C7F] uppercase">Who We Are</span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-[#0D0D0D] leading-[1.1] max-w-4xl">
            Strategic Architects of{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#038C7F] to-[#F2780C]">
              Brand Perception
            </span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Left Content */}
          <div className={`lg:col-span-7 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {/* Quote Block */}
            <div className="relative mb-6 sm:mb-8 pl-4 sm:pl-5 border-l-4 border-[#038C7F]">
              <i className="ri-double-quotes-l absolute -top-2 sm:-top-3 -left-1 sm:-left-2 text-4xl sm:text-5xl text-[#038C7F]/10"></i>
              <p className="text-base sm:text-lg md:text-xl font-serif italic text-[#0D0D0D] leading-relaxed">
                "Äsili" means <strong className="text-[#038C7F]">origin</strong> or{' '}
                <strong className="text-[#038C7F]">essence</strong> in Swahili. We believe sustainable brands are built from authenticity.
              </p>
            </div>

            <div className="space-y-3 sm:space-y-4 text-sm text-gray-600 leading-relaxed mb-6 sm:mb-8">
              <p>
                Äsili Visuals is an <strong className="text-[#0D0D0D]">integrated advertising and brand communication firm</strong> based in Kampala, Uganda, serving clients across East Africa with a continental growth ambition.
              </p>
              <p>
                We help businesses, institutions, and governments define their identity, articulate their value, and communicate consistently across traditional and modern platforms.
              </p>
              <p>
                We develop <strong className="text-[#0D0D0D]">structured communication systems</strong> that strengthen credibility, expand markets, and enhance long-term brand equity.
              </p>
            </div>

            {/* Highlight Box */}
            <div className="relative bg-[#0D0D0D] rounded-xl sm:rounded-2xl p-4 sm:p-6 overflow-hidden group hover:shadow-2xl transition-shadow duration-500">
              <div className="absolute top-0 right-0 w-20 sm:w-24 h-20 sm:h-24 bg-[#038C7F]/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
              <div className="relative flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#038C7F] rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-lightbulb-flash-line text-xl sm:text-2xl text-white"></i>
                </div>
                <div>
                  <p className="text-base sm:text-lg md:text-xl font-bold text-white leading-tight">
                    Brand Communication as a System
                  </p>
                  <p className="text-sm sm:text-base text-gray-300 leading-tight mt-1">
                    Consistency builds trust. Trust builds markets.
                  </p>
                </div>
              </div>
            </div>

            {/* Regional Coverage */}
            <div className="mt-6 sm:mt-8">
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <i className="ri-map-pin-line text-base sm:text-lg text-[#038C7F]"></i>
                <h3 className="text-xs sm:text-sm font-bold text-[#0D0D0D] uppercase tracking-wider">Service Region: East Africa</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {countries.map((country, index) => (
                  <div
                    key={country.name}
                    className={`group flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-white border border-gray-200 rounded-full hover:border-[#038C7F] hover:shadow-lg transition-all duration-300 cursor-default ${
                      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
                    }`}
                    style={{ transitionDelay: `${400 + index * 100}ms` }}
                  >
                    <span className="text-sm sm:text-base">{country.flag}</span>
                    <span className="text-xs sm:text-sm font-medium text-[#0D0D0D] group-hover:text-[#038C7F] transition-colors">
                      {country.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Panel */}
          <div className={`lg:col-span-5 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {/* Stats Card */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6 border border-gray-100">
              <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="text-center p-2 sm:p-3 bg-[#038C7F]/5 rounded-lg sm:rounded-xl">
                  <div className="text-2xl sm:text-3xl font-black text-[#038C7F]">{counters.projects}+</div>
                  <div className="text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-wider mt-0.5 sm:mt-1">Projects</div>
                </div>
                <div className="text-center p-2 sm:p-3 bg-[#F2780C]/5 rounded-lg sm:rounded-xl">
                  <div className="text-2xl sm:text-3xl font-black text-[#F2780C]">{counters.countries}</div>
                  <div className="text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-wider mt-0.5 sm:mt-1">Countries</div>
                </div>
                <div className="text-center p-2 sm:p-3 bg-[#0D0D0D]/5 rounded-lg sm:rounded-xl">
                  <div className="text-2xl sm:text-3xl font-black text-[#0D0D0D]">{counters.years}+</div>
                  <div className="text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-wider mt-0.5 sm:mt-1">Years</div>
                </div>
              </div>

              {/* 360 Badge */}
              <div className="relative flex items-center justify-center py-4 sm:py-6">
                <div
                  className="absolute w-24 sm:w-32 h-24 sm:h-32 border-2 border-dashed border-[#038C7F]/20 rounded-full animate-spin"
                  style={{ animationDuration: '20s' }}
                ></div>
                <div
                  className="absolute w-18 sm:w-24 h-18 sm:h-24 border-2 border-dashed border-[#F2780C]/20 rounded-full animate-spin"
                  style={{ animationDuration: '15s', animationDirection: 'reverse' }}
                ></div>
                <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#038C7F] to-[#0D0D0D] rounded-full flex items-center justify-center shadow-xl">
                  <div className="text-center">
                    <div className="text-lg sm:text-xl font-black text-white">360°</div>
                    <div className="text-[7px] sm:text-[8px] font-bold text-white/70 uppercase">System</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advantages Grid */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              {advantages.map((item, index) => (
                <div
                  key={item.title}
                  className={`group bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-100 hover:border-[#038C7F] hover:shadow-lg transition-all duration-300 cursor-default ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
                  }`}
                  style={{ transitionDelay: `${600 + index * 100}ms` }}
                >
                  <div
                    className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center mb-2 transition-colors duration-300 ${
                      index % 2 === 0 ? 'bg-[#038C7F]/10 group-hover:bg-[#038C7F]' : 'bg-[#F2780C]/10 group-hover:bg-[#F2780C]'
                    }`}
                  >
                    <i
                      className={`${item.icon} text-base sm:text-lg ${
                        index % 2 === 0 ? 'text-[#038C7F] group-hover:text-white' : 'text-[#F2780C] group-hover:text-white'
                      } transition-colors duration-300`}
                    ></i>
                  </div>
                  <h4 className="text-[11px] sm:text-xs font-bold text-[#0D0D0D] mb-0.5 sm:mb-1">{item.title}</h4>
                  <p className="text-[10px] sm:text-[11px] text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className={`grid sm:grid-cols-2 gap-3 sm:gap-4 mt-8 sm:mt-12 transition-all duration-700 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="group relative bg-gradient-to-br from-[#038C7F] to-[#026b61] rounded-xl sm:rounded-2xl p-4 sm:p-6 overflow-hidden">
            <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="relative">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="w-8 h-8 sm:w-9 sm:h-9 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-rocket-2-line text-base sm:text-lg text-white"></i>
                </div>
                <span className="text-[10px] sm:text-xs font-bold text-white/70 uppercase tracking-wider">Our Mission</span>
              </div>
              <p className="text-xs sm:text-sm text-white leading-relaxed">
                To empower enterprises, organizations, and governments with integrated advertising and communication systems that strengthen credibility, expand markets, and enhance long-term brand equity.
              </p>
            </div>
          </div>

          <div className="group relative bg-gradient-to-br from-[#F2780C] to-[#d66a0a] rounded-xl sm:rounded-2xl p-4 sm:p-6 overflow-hidden">
            <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="relative">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="w-8 h-8 sm:w-9 sm:h-9 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-eye-line text-base sm:text-lg text-white"></i>
                </div>
                <span className="text-[10px] sm:text-xs font-bold text-white/70 uppercase tracking-wider">Our Vision</span>
              </div>
              <p className="text-xs sm:text-sm text-white leading-relaxed">
                To become one of Africa's leading marketing and advertising firms, supporting businesses and nations to advertise effectively, present credible brands, and communicate powerfully across traditional and digital platforms.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
